"use client"

/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react"
import {
	IconArrowDownToLineOutline18 as Download,
	IconArrowsExpandOutline18 as Maximize,
	IconArrowsExpandOutline18 as Minimize,
	IconMediaPauseOutline18 as Pause,
	IconMediaPlayOutline18 as Play,
	IconVolumeMinusOutline18 as Volume1,
	IconVolumeOutline18 as Volume2,
	IconVolumeOffOutline18 as VolumeX,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import { TooltipProvider } from "./internal/tooltip"
import { Tooltip } from "./tooltip"

function formatTime(seconds: number): string {
	if (!Number.isFinite(seconds)) return "0:00"
	const m = Math.floor(seconds / 60)
	const s = Math.floor(seconds % 60)
	return `${m}:${s.toString().padStart(2, "0")}`
}

interface VideoPlayerProps {
	src: string
	/** Styles the outer container (sizing, spacing). Defaults to aspect-video. */
	className?: string
	poster?: string
	preload?: "auto" | "metadata" | "none"
	autoPlay?: boolean
	loop?: boolean
	muted?: boolean
	playsInline?: boolean
}

export const VideoPlayer = memo(
	forwardRef<HTMLVideoElement, VideoPlayerProps>(
		(
			{
				src,
				className,
				poster,
				preload = "metadata",
				autoPlay,
				loop,
				muted,
				playsInline,
			},
			ref,
		) => {
			const videoRef = useRef<HTMLVideoElement>(null)
			const containerRef = useRef<HTMLDivElement>(null)
			const progressRef = useRef<HTMLDivElement>(null)
			const volumeRef = useRef<HTMLDivElement>(null)
			const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

			useImperativeHandle(ref, () => videoRef.current!)

			const [isPlaying, setIsPlaying] = useState(false)
			const [currentTime, setCurrentTime] = useState(0)
			const [duration, setDuration] = useState(0)
			const [volume, setVolume] = useState(1)
			const [isMuted, setIsMuted] = useState(!!muted)
			const [isFullscreen, setIsFullscreen] = useState(false)
			const [showControls, setShowControls] = useState(true)
			const [isSeeking, setIsSeeking] = useState(false)

			const scheduleHide = useCallback(() => {
				if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
				hideTimerRef.current = setTimeout(() => setShowControls(false), 2500)
			}, [])

			useEffect(() => {
				const v = videoRef.current
				if (!v) return

				const onPlay = () => {
					setIsPlaying(true)
					scheduleHide()
				}
				const onPause = () => {
					setIsPlaying(false)
					setShowControls(true)
					if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
				}
				const onTimeUpdate = () => {
					if (!isSeeking) setCurrentTime(v.currentTime)
				}
				const onLoaded = () => setDuration(v.duration)
				const onEnded = () => {
					setIsPlaying(false)
					setShowControls(true)
				}
				const onVolChange = () => {
					setIsMuted(v.muted)
					setVolume(v.volume)
				}

				v.addEventListener("play", onPlay)
				v.addEventListener("pause", onPause)
				v.addEventListener("timeupdate", onTimeUpdate)
				v.addEventListener("loadedmetadata", onLoaded)
				v.addEventListener("ended", onEnded)
				v.addEventListener("volumechange", onVolChange)

				return () => {
					v.removeEventListener("play", onPlay)
					v.removeEventListener("pause", onPause)
					v.removeEventListener("timeupdate", onTimeUpdate)
					v.removeEventListener("loadedmetadata", onLoaded)
					v.removeEventListener("ended", onEnded)
					v.removeEventListener("volumechange", onVolChange)
				}
			}, [isSeeking, scheduleHide])

			useEffect(() => {
				const handler = () => setIsFullscreen(!!document.fullscreenElement)
				document.addEventListener("fullscreenchange", handler)
				return () => document.removeEventListener("fullscreenchange", handler)
			}, [])

			useEffect(
				() => () => {
					if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
				},
				[],
			)

			const togglePlay = useCallback(() => {
				const v = videoRef.current
				if (!v) return
				if (v.paused) void v.play().catch(() => undefined)
				else v.pause()
			}, [])

			const toggleMute = useCallback(() => {
				const v = videoRef.current
				if (!v) return
				v.muted = !v.muted
			}, [])

			const handleVolumeDown = useCallback(
				(e: React.MouseEvent<HTMLDivElement>) => {
					const bar = volumeRef.current
					const v = videoRef.current
					if (!bar || !v) return

					const setVol = (ev: MouseEvent | React.MouseEvent) => {
						const rect = bar.getBoundingClientRect()
						const pos = Math.max(
							0,
							Math.min(1, (rect.bottom - ev.clientY) / rect.height),
						)
						v.volume = pos
						if (pos > 0 && v.muted) v.muted = false
						if (pos === 0) v.muted = true
					}
					setVol(e)

					const onMove = (ev: MouseEvent) => setVol(ev)
					const onUp = () => {
						window.removeEventListener("mousemove", onMove)
						window.removeEventListener("mouseup", onUp)
					}
					window.addEventListener("mousemove", onMove)
					window.addEventListener("mouseup", onUp)
				},
				[],
			)

			const toggleFullscreen = useCallback(() => {
				if (document.fullscreenElement) {
					void document.exitFullscreen()
				} else if (document.fullscreenEnabled) {
					void containerRef.current?.requestFullscreen()
				} else {
					// Fallback for iframes without fullscreen permission
					window.open(src, "_blank")
				}
			}, [src])

			const handleMouseMove = useCallback(() => {
				setShowControls(true)
				if (isPlaying) scheduleHide()
			}, [isPlaying, scheduleHide])

			const handleSeekDown = useCallback(
				(e: React.MouseEvent<HTMLDivElement>) => {
					const bar = progressRef.current
					const v = videoRef.current
					if (!bar || !v || !duration) return

					setIsSeeking(true)

					const seek = (ev: MouseEvent | React.MouseEvent) => {
						const rect = bar.getBoundingClientRect()
						const pos = Math.max(
							0,
							Math.min(1, (ev.clientX - rect.left) / rect.width),
						)
						v.currentTime = pos * duration
						setCurrentTime(pos * duration)
					}
					seek(e)

					const onMove = (ev: MouseEvent) => seek(ev)
					const onUp = () => {
						setIsSeeking(false)
						window.removeEventListener("mousemove", onMove)
						window.removeEventListener("mouseup", onUp)
					}
					window.addEventListener("mousemove", onMove)
					window.addEventListener("mouseup", onUp)
				},
				[duration],
			)

			const progress = duration > 0 ? (currentTime / duration) * 100 : 0
			const controlsVisible = showControls || !isPlaying

			return (
				<TooltipProvider>
					<div
						ref={containerRef}
						className={cn(
							"group relative overflow-hidden rounded-md bg-black",
							isFullscreen
								? "flex h-full w-full items-center justify-center"
								: "aspect-video",
							!isFullscreen && className,
						)}
						onMouseMove={handleMouseMove}
						onMouseLeave={() => {
							if (isPlaying) scheduleHide()
						}}
					>
						<video
							ref={videoRef}
							src={src}
							poster={poster}
							preload={preload}
							autoPlay={autoPlay}
							loop={loop}
							muted={muted}
							playsInline={playsInline}
							className="h-full w-full object-contain"
							onClick={togglePlay}
						>
							<track kind="captions" label="None" />
						</video>

						{/* Center play button */}
						{!isPlaying && (
							<button
								type="button"
								className="absolute inset-0 z-10 flex items-center justify-center"
								onClick={togglePlay}
								aria-label="Play"
							>
								<div className="flex size-11 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-transform hover:scale-110">
									<Play className="ml-0.5 size-5 fill-current" />
								</div>
							</button>
						)}

						{/* Bottom controls */}
						<div
							className={cn(
								"absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/60 via-black/30 to-transparent px-3 pb-2 pt-8 transition-opacity duration-200",
								controlsVisible
									? "opacity-100"
									: "pointer-events-none opacity-0",
							)}
							onClick={(e) => e.stopPropagation()}
						>
							{/* Progress bar */}
							<div
								ref={progressRef}
								className="group/seek mb-1.5 flex h-3 items-center"
								onMouseDown={handleSeekDown}
							>
								<div className="relative h-1 w-full rounded-full bg-white/25 transition-[height] duration-150 group-hover/seek:h-1.5">
									<div
										className="absolute inset-y-0 left-0 rounded-full bg-white"
										style={{ width: `${progress}%` }}
									/>
									<div
										className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 shadow-sm transition-opacity duration-150 group-hover/seek:opacity-100"
										style={{ left: `${progress}%` }}
									/>
								</div>
							</div>

							{/* Button row */}
							<div className="flex items-center gap-1.5">
								<Tooltip label={isPlaying ? "Pause" : "Play"} side="bottom">
									<button
										type="button"
										onClick={togglePlay}
										className="flex size-6 items-center justify-center text-white/90 hover:text-white"
										aria-label={isPlaying ? "Pause" : "Play"}
									>
										{isPlaying ? (
											<Pause className="size-3.5 fill-current" />
										) : (
											<Play className="ml-0.5 size-3.5 fill-current" />
										)}
									</button>
								</Tooltip>

								{/* Volume control with hover slider */}
								<div className="group/vol relative flex items-center">
									<Tooltip label={"Mute"} side="bottom">
										<button
											type="button"
											onClick={toggleMute}
											className="flex size-6 items-center justify-center text-white/90 hover:text-white"
											aria-label={isMuted ? "Unmute" : "Mute"}
										>
											{isMuted || volume === 0 ? (
												<VolumeX className="size-3.5" />
											) : volume < 0.5 ? (
												<Volume1 className="size-3.5" />
											) : (
												<Volume2 className="size-3.5" />
											)}
										</button>
									</Tooltip>

									{/* Vertical volume slider */}
									<div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 pb-2 opacity-0 transition-opacity duration-150 group-hover/vol:pointer-events-auto group-hover/vol:opacity-100">
										<div className="flex h-20 w-7 items-center justify-center rounded-md bg-white/20 backdrop-blur-sm">
											<div
												ref={volumeRef}
												className="relative h-14 w-1 rounded-full bg-white/25"
												onMouseDown={handleVolumeDown}
											>
												<div
													className="absolute inset-x-0 bottom-0 rounded-full bg-white"
													style={{
														height: `${(isMuted ? 0 : volume) * 100}%`,
													}}
												/>
												<div
													className="absolute left-1/2 size-2.5 -translate-x-1/2 translate-y-1/2 rounded-full bg-white shadow-sm"
													style={{
														bottom: `${(isMuted ? 0 : volume) * 100}%`,
													}}
												/>
											</div>
										</div>
									</div>
								</div>

								<span className="select-none text-xs tabular-nums text-white">
									{formatTime(currentTime)}
									{" / "}
									{formatTime(duration)}
								</span>

								<div className="flex-1" />

								<Tooltip label="Download" side="bottom">
									<a
										href={src}
										download
										onClick={(e) => e.stopPropagation()}
										className="flex size-6 items-center justify-center text-white/90 hover:text-white"
										aria-label="Download"
									>
										<Download className="size-3.5" />
									</a>
								</Tooltip>

								<Tooltip label="Fullscreen" side="bottom">
									<button
										type="button"
										onClick={toggleFullscreen}
										className="flex size-6 items-center justify-center text-white/90 hover:text-white"
										aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
									>
										{isFullscreen ? (
											<Minimize className="size-3.5" />
										) : (
											<Maximize className="size-3.5" />
										)}
									</button>
								</Tooltip>
							</div>
						</div>
					</div>
				</TooltipProvider>
			)
		},
	),
)
